/**
 * AUDIO EDITING — the Studio capability boundary.
 *
 * This module is now mixed-mode rather than all-or-nothing. When `API_BASE_URL` is
 * configured it forwards the audio-edit routes the backend really exposes
 * (trim/cut/split/crossfade/insertSilence/fade/normalize/tempo/pitch/mix/master) and
 * assembles the real media-download URLs for exports and stem downloads.
 *
 * Only the routes Lyrixa still does not expose stay intentionally unsupported and throw
 * `UnsupportedCapabilityError`. Keeping that split explicit in one place is the point:
 * callers can ask `supports()`, build typed requests, and either send them to the backend
 * or surface an honest capability message.
 *
 * The generation provider still matters for the simulation-backed Studio tools, but it is
 * not the reason these unsupported operations refuse. The provider-independent reason is
 * that Lyrixa's own backend does not implement those routes yet.
 */
import { API_BASE_URL } from '../config';
import { EXPORT_PROFILES } from '../lib/exportSize';
import type { CompressorSettings, DelaySettings, EqGains, ReverbSettings } from '../lib/stems';
import { http } from './http';
import type { BlobResponse } from './http';
import type { Song, StemSplitJob, StudioActionJob } from '../types/domain';
import type { TrimRange } from '../lib/trim';
import { waitForStudioAction, waitForStudioJob } from './studioActionJobs';

/**
 * Thrown by every function in this module.
 *
 * Its own class, and not `ApiError`, because it is not a failed request: nothing was
 * sent and no status came back. A caller distinguishing "the server said no" from
 * "this cannot be asked for yet" needs those to be different types — the first is
 * worth a retry button and the second never is.
 */
export class UnsupportedCapabilityError extends Error {
  readonly name = 'UnsupportedCapabilityError';

  /** The capability key, so a caller can look it up in AUDIO_EDIT_CAPABILITIES. */
  readonly capability: AudioEditCapability;

  constructor(capability: AudioEditCapability, message: string) {
    super(message);
    this.capability = capability;
  }
}

/**
 * The audio operations the Studio's tools would need.
 *
 * The first five predate this pass. The rest were added with the Edit, Tempo & Pitch,
 * Mix, Master and Export workspaces, and they are all here for one reason: every one of
 * them takes existing audio as its input, which is the single thing this app does not
 * have. Listing them is the honest form of "not implemented" — a key with a stated
 * missing piece is checkable, whereas a tool that is simply absent from the product
 * cannot be told apart from one nobody has thought about.
 */
export type AudioEditCapability =
  | 'trim'
  | 'extend'
  | 'remix'
  | 'replaceSection'
  | 'separateStems'
  // ---- destructive edits on a region of the track (the Edit workspace) ----
  | 'cut'
  | 'split'
  | 'fade'
  | 'crossfade'
  | 'insertSilence'
  | 'normalize'
  // ---- whole-track transforms ----
  | 'tempo'
  | 'pitch'
  | 'mix'
  | 'master'
  | 'exportAudio';

/**
 * What each Studio audio operation actually requires, and what stands in its way.
 *
 * One record rather than a comment in five panels, so "which of our tools are real?"
 * has a single answer that cannot drift out of date panel by panel. `simulated` marks
 * the four that predate this check and currently run a timer; `trim` is false because
 * it ships as a request that is never sent.
 */
export const AUDIO_EDIT_CAPABILITIES: Record<
  AudioEditCapability,
  {
    /** UI label, matching the tool's name. */
    label: string;
    /** Whether the Lyria 3 Interactions API supports it. All false. */
    supportedByLyria3: false;
    /** Whether the app currently runs a simulation for it. */
    simulated: boolean;
    /** The missing piece, in one line, for the report and the UI notice. */
    missing: string;
  }
> = {
  trim: {
    label: 'Trim',
    supportedByLyria3: false,
    simulated: false,
    missing:
      'An audio service that can cut a clip and store the result. Lyria 3 generates ' +
      'audio but cannot take audio as input, so a trim cannot be expressed as a ' +
      'generation request.',
  },
  extend: {
    label: 'Extend',
    supportedByLyria3: false,
    simulated: true,
    missing:
      'Continuation is handled by the backend Studio provider, which reuses the song\'s stored ' +
      'audio and runs the extend asynchronously before appending the result as a new version.',
  },
  remix: {
    label: 'Remix',
    supportedByLyria3: false,
    simulated: true,
    missing:
      'Re-generation conditioned on an existing clip. A fresh prompt in the requested ' +
      'style is a new song, not a remix of this one.',
  },
  replaceSection: {
    label: 'Replace Section',
    supportedByLyria3: false,
    simulated: true,
    missing:
      'Inpainting over a time range, which the Interactions API does not expose — it ' +
      'has no notion of a position within a clip.',
  },
  separateStems: {
    label: 'Split Stems',
    supportedByLyria3: false,
    simulated: false,
    missing:
      'Source separation. Lyria 3 only generates audio and cannot decompose an existing ' +
      'clip into stems, so this runs through Replicate Demucs instead — a separate provider ' +
      'that returns vocals, bass, drums, guitar, piano and other as individual files.',
  },

  /*
    ---- ADDED WITH THE EDIT / TEMPO & PITCH / MIX / MASTER WORKSPACES ----

    `simulated: false` on every one of them, and that is the point: none of these runs a
    timer, appends a version or reports success. Each has a panel with real controls and
    a real computed readout, over a submit that is disabled and says why. They are the
    same missing capability as Trim rather than the same shape as Extend.
  */
  cut: {
    label: 'Cut',
    supportedByLyria3: false,
    simulated: false,
    missing:
      'An audio service that can remove a region and join what remains. The arithmetic ' +
      'of which region and how long the result would be is done client-side; the encode ' +
      'is not something a browser can host.',
  },
  split: {
    label: 'Split',
    supportedByLyria3: false,
    simulated: false,
    missing:
      'An audio service that can write two files from one at a chosen point, plus a ' +
      'versioning story for the pair — one song becoming two is not an edit to a song.',
  },
  fade: {
    label: 'Fade in / out',
    supportedByLyria3: false,
    simulated: false,
    missing:
      'A gain envelope applied to a stored file. Nothing here can be previewed either: ' +
      'there is no audio to ramp, so a fade cannot even be demonstrated locally.',
  },
  crossfade: {
    label: 'Crossfade',
    supportedByLyria3: false,
    simulated: false,
    missing:
      'Two audio regions to overlap and a service to overlap them. It needs whatever ' +
      'cut and split produce, so it is behind both of those rather than beside them.',
  },
  insertSilence: {
    label: 'Insert silence',
    supportedByLyria3: false,
    simulated: false,
    missing:
      'An audio service that can splice a gap into a stored file. The one operation ' +
      'here that needs no model at all, and still needs a file and somewhere to put it.',
  },
  normalize: {
    label: 'Normalize',
    supportedByLyria3: false,
    simulated: false,
    missing:
      'Loudness measurement of the real audio. A target in LUFS is meaningless without ' +
      'a measured integrated loudness to move, and nothing has ever measured this track.',
  },
  tempo: {
    label: 'Tempo',
    supportedByLyria3: false,
    simulated: false,
    missing:
      'Time stretching, which needs a phase-vocoder or similar DSP service. Lyria 3 ' +
      'takes no audio input, so re-generating at a new BPM would be a different song ' +
      'rather than this one at a different speed.',
  },
  pitch: {
    label: 'Pitch',
    supportedByLyria3: false,
    simulated: false,
    missing:
      'Pitch shifting that preserves duration. The same DSP service tempo needs, and ' +
      'the same reason a re-generation is not a substitute: it would not be this take.',
  },
  mix: {
    label: 'Mix',
    supportedByLyria3: false,
    simulated: false,
    missing:
      'Balance needs separated stems. EQ, compressor, reverb and delay can run on the ' +
      'full song audio, but stem balance itself has nothing separate to rebalance until ' +
      'source separation has produced individual tracks.',
  },
  master: {
    label: 'Master',
    supportedByLyria3: false,
    simulated: false,
    missing:
      'A mastering chain — measurement, EQ, compression and limiting against a loudness ' +
      'target. Lyria 3 has no mastering operation, so this is an external audio service, ' +
      'not a generation request.',
  },
  exportAudio: {
    label: 'Export audio',
    supportedByLyria3: false,
    simulated: false,
    missing:
      'An audio file to encode. Downloads currently write a text details file and say ' +
      'so; the format, quality and size arithmetic is real, and the bytes are not.',
  },
};

/** The request body a real trim endpoint would receive. Never sent. */
export interface TrimRequest {
  songId: string;
  /** Version explicitly loaded as the source HEAD for this edit, when any. */
  sourceVersionId?: string;
  /** Seconds from the start of the track. Validated by lib/trim.resolveTrimRange. */
  start: number;
  end: number;
}

/** POST /songs/:id/studio/split — one song becoming two at a chosen point. */
export interface SplitRequest {
  songId: string;
  /** Version explicitly loaded as the source HEAD for this edit, when any. */
  sourceVersionId?: string;
  /** Seconds from the start of the track. Validated against the track's duration server-side. */
  at: number;
}

/** What `POST /songs/:id/studio/split` answers with: two new version-backed halves on one song. */
export interface SplitResult {
  /** The visible parent song, now carrying the two new split versions in Version History. */
  song: Song;
  /** The hidden backing song for the first half's version. */
  firstSong: Song;
  /** The hidden backing song for the second half's version. */
  secondSong: Song;
}

/** POST /songs/:id/studio/crossfade — joins two songs' audio into a third, new song. */
export interface CrossfadeRequest {
  /** The first song; also the URL's :id. */
  songId: string;
  /** The second song to blend in. */
  secondSongId: string;
  /** Seconds the two clips overlap by. */
  seconds: number;
}

/**
 * The request bodies the whole-track and region operations receive.
 *
 * Tempo and pitch are sent for real now (see `audioEditApi.setTempo`/`setPitch`); the
 * region/mix/master bodies below are still `satisfies`-checked and thrown on. Written out
 * rather than left as `unknown` because the shape of the call is the deliverable: each is
 * `satisfies`-checked inside the method that sends it, so the types cannot drift away from
 * the arguments the UI already collects, and wiring an endpoint is a change to one function
 * body.
 */

/** POST /songs/:id/studio/tempo — time stretch to a new BPM. */
export interface TempoRequest {
  songId: string;
  /** The BPM the track is at now, as recorded on the Song. */
  fromBpm: number;
  /** The requested BPM. Validated by lib/tempoPitch.resolveTempo. */
  toBpm: number;
  /**
   * Whether the transform must leave pitch alone. Always true from the UI: a stretch
   * that also moves pitch is a tape-speed change, which is a different operation and
   * would need to be asked for separately.
   */
  preservePitch: boolean;
  /** Version explicitly loaded as the source HEAD for this edit, when any. */
  sourceVersionId?: string;
}

/** POST /songs/:id/studio/pitch — shift by semitones, duration unchanged. */
export interface PitchRequest {
  songId: string;
  /** −12…+12. Validated by lib/tempoPitch.clampSemitones. */
  semitones: number;
  preserveTempo: boolean;
  /** Version explicitly loaded as the source HEAD for this edit, when any. */
  sourceVersionId?: string;
}

/** POST /songs/:id/studio/insertSilence — splices a gap into a stored file at a chosen point. */
export interface InsertSilenceRequest {
  songId: string;
  /** Version explicitly loaded as the source HEAD for this edit, when any. */
  sourceVersionId?: string;
  /** Seconds from the start of the track where the gap is inserted. */
  at: number;
  /** How long the inserted gap is, in seconds. */
  duration: number;
}

/** POST /songs/:id/studio/fade — fades the stored file in at the start and out at the end. */
export interface FadeRequest {
  songId: string;
  /** Version explicitly loaded as the source HEAD for this edit, when any. */
  sourceVersionId?: string;
  /** How long the fade-in and fade-out each last, in seconds. */
  seconds: number;
}

/** POST /songs/:id/regions — one destructive edit on a region of the track. */
export interface RegionEditRequest {
  songId: string;
  operation: 'cut' | 'split' | 'crossfade' | 'normalize';
  /** Seconds from the start. Absent for whole-track operations like normalize. */
  start?: number;
  end?: number;
}

/** POST /songs/:id/mix — balance over stems when present, plus whole-track effects. */
export interface MixRequest {
  songId: string;
  /** Version explicitly loaded as the source HEAD for this edit, when any. */
  sourceVersionId?: string;
  /**
   * Stem balance controls.
   *
   * Optional on purpose: Balance is the only part of Mix that needs separated stems.
   * Effect-only preview/apply works directly on the song's current audio without them.
   */
  tracks?: Array<{
    /** The stem's key, from lib/stems.STEM_TRACKS. */
    id: string;
    /** 0…1. */
    volume: number;
    /** −1 (hard left) … 1 (hard right). */
    pan: number;
    muted: boolean;
  }>;
  /** Five fixed EQ gains in dB, from -12 to +12. */
  eq: EqGains;
  /** One broadband compressor after EQ. */
  compressor: CompressorSettings;
  /** Delay-based room reverb after compression. */
  reverb: ReverbSettings;
  /** Feedback delay after the reverb stage. */
  delay: DelaySettings;
}

export interface MixAnalysisTrack {
  id: string;
  volume: number;
  pan: number;
  muted: boolean;
}

export interface MixAnalysis {
  tracks: MixAnalysisTrack[];
  eq: EqGains;
  compressor: CompressorSettings;
  reverb: ReverbSettings;
  delay?: DelaySettings;
}

/** POST /songs/:id/master — loudness and character. */
export interface MasterRequest {
  songId: string;
  /** Version explicitly loaded as the source HEAD for this edit, when any. */
  sourceVersionId?: string;
  /** Target key from lib/mastering.MASTER_TARGETS. */
  target: string;
  /** Character key from lib/mastering.MASTER_CHARACTERS. */
  character: string;
  /** The target's integrated loudness in LUFS, carried so the body is self-describing. */
  targetLufs: number;
  /** The target's true-peak ceiling in dBTP. */
  truePeak: number;
}

export interface MasterAnalysis {
  integratedLufs: number;
  truePeakDbtp: number;
  loudnessRange: number;
  threshold: number;
  targetOffset: number;
}

export type StoredStemType = 'vocals' | 'bass' | 'drums' | 'guitar' | 'piano' | 'other';

export interface StemAvailabilityResult {
  stemTypes: StoredStemType[];
  job?: StemSplitJob;
}

/** POST /songs/:id/export — encode and return a file. */
export interface ExportAudioRequest {
  songId: string;
  /** The version-specific stem job whose assets should be exported. */
  stemJobId?: string;
  format: 'mp3' | 'wav';
  /** Quality key from lib/exportSize.EXPORT_PROFILES. */
  quality: string;
  /** Which part of the track. `stems` returns an archive. */
  content: 'full' | 'instrumental' | 'acapella' | 'stems';
  /**
   * One stem rather than the archive, keyed from lib/stems.STEM_TRACKS.
   *
   * Only meaningful with `content: 'stems'`. Present because the Stems panel offers each
   * track individually as well as a ZIP — five rows and a bundle — and without this the
   * five rows would have no request to make and would be the only download surface with
   * no seam behind it.
   */
  stemName?: string;
}

/**
 * What the export/download boundary answers with.
 *
 * This is no longer hypothetical. When a backend base URL is configured, `exportAudio()`
 * returns the direct download URL plus a suggested filename the browser should save under.
 * The method still does not encode bytes on the client — it only assembles the URL the
 * backend serves.
 *
 * A URL rather than bytes keeps large downloads in the browser's normal download flow.
 * `downloadFromUrl()` can save a hosted URL, a signed URL, or a caller-owned blob URL
 * through the same receiving seam.
 */
export interface ExportAudioResult {
  /** Where to fetch the encoded file. Hosted, signed or blob — the caller does not care. */
  url: string;
  /** Suggested filename, including extension. The server names the file, not the client. */
  filename: string;
  /** Echoed from the request, so a caller can label what it received without re-deriving it. */
  format: 'mp3' | 'wav';
  /** Actual encoded size. Absent if the server streams without knowing it up front. */
  byteSize?: number;
  /**
   * Seconds until `url` stops working, for a signed URL.
   *
   * Optional because a permanently hosted asset has no expiry. A UI that caches the URL
   * needs to know it may go stale; today nothing caches it, and this is here so that the
   * first thing that does has somewhere to read the answer from.
   */
  expiresInSec?: number;
}

/**
 * Builds the export request for a chosen format and quality tier.
 *
 * Exported and pure so it can be asserted without a server, matching
 * `audioUploadApi.buildAudioUploadRequest` and `lyria.buildLyriaRequest`: being able to
 * check the exact shape is what distinguishes a real boundary from an intention.
 *
 * THE FORMAT WINS OVER THE TIER, and that rule is here rather than in the panel because it
 * is a fact about the request. The Download Center has an MP3 button, a WAV button and a
 * separate quality selector, so "WAV" and "Standard (MP3 192)" can be chosen together —
 * the button says which container was asked for, and a tier naming a different one cannot
 * describe it. So the tier is used when its format agrees and the first tier of the
 * requested format is substituted when it does not, which is the only reading that leaves
 * both controls meaning what they say.
 */
export function buildExportAudioRequest(input: {
  songId: string;
  stemJobId?: string;
  /** As the UI labels it — "MP3"/"WAV", any case. */
  format: string;
  /** The selected tier's id from lib/exportSize.EXPORT_PROFILES. */
  quality: string;
  content?: ExportAudioRequest['content'];
  stemName?: string;
}): ExportAudioRequest {
  const format: 'mp3' | 'wav' = input.format.toLowerCase() === 'wav' ? 'wav' : 'mp3';
  const chosen = EXPORT_PROFILES.find((p) => p.id === input.quality);
  const quality =
    chosen && chosen.format === format
      ? chosen.id
      : (EXPORT_PROFILES.find((p) => p.format === format)?.id ?? EXPORT_PROFILES[0].id);

  return {
    songId: input.songId,
    ...(input.stemJobId ? { stemJobId: input.stemJobId } : {}),
    format,
    quality,
    content: input.content ?? 'full',
    ...(input.stemName ? { stemName: input.stemName } : {}),
  };
}

const STORED_AUDIO_ROUTE_PREFIX = '/api/media/audio/';

/** True once a song points at Lyrixsa's own stored-media route rather than a temporary provider URL. */
export function hasStoredAudioFile(song: Pick<Song, 'audioUrl'> | null | undefined): boolean {
  return typeof song?.audioUrl === 'string' && song.audioUrl.startsWith(STORED_AUDIO_ROUTE_PREFIX);
}

/**
 * A generated song whose playback URL still comes from the provider, while Lyrixsa stores it.
 *
 * These are the songs that resolve themselves after generation without a page refresh: the
 * backend updates the Song record from the temporary provider URL to `/api/media/audio/...`
 * once background persistence finishes. A song with no audio URL at all is a different case —
 * there is nothing ready yet, but also nothing this client can poll into existence from a
 * temporary playback URL.
 */
export function needsStoredAudioFinalization(
  song: Pick<Song, 'songOrigin' | 'audioUrl'> | null | undefined
): boolean {
  return song?.songOrigin === 'generated' && typeof song.audioUrl === 'string' && !hasStoredAudioFile(song);
}

/** What a user should be told when a stored-audio action cannot run yet. */
export function storedAudioRequirementMessage(
  song: Pick<Song, 'songOrigin' | 'audioUrl'> | null | undefined,
  action = 'use this action'
): string {
  if (needsStoredAudioFinalization(song)) {
    return 'Finalizing audio… this song will unlock automatically as soon as Lyrixsa finishes storing it.';
  }
  return `This song needs a stored audio file before you can ${action}.`;
}

export const audioEditApi = {
  /**
   * Whether a capability can be performed.
   *
   * Still conservative: only the routes this backend actually exposes return true. A base URL
   * alone is not evidence that every audio-edit feature exists, which is why mix and the
   * still-unimplemented region/export routes remain false here until their server routes land.
   */
  supports(capability: AudioEditCapability): boolean {
    if (!API_BASE_URL) return false;
    return (
      capability === 'trim' ||
      capability === 'cut' ||
      capability === 'split' ||
      capability === 'crossfade' ||
      capability === 'insertSilence' ||
      capability === 'fade' ||
      capability === 'normalize' ||
      capability === 'tempo' ||
      capability === 'pitch' ||
      capability === 'mix' ||
      capability === 'master' ||
      capability === 'exportAudio'
    );
  },

  /** POST /songs/:id/studio/trim */
  async trim(songId: string, range: TrimRange, sourceVersionId?: string | null): Promise<Song> {
    const request = { songId, ...(sourceVersionId ? { sourceVersionId } : null), ...range } satisfies TrimRequest;
    if (!API_BASE_URL) {
      throw new UnsupportedCapabilityError(
        'trim',
        'Trimming audio is not available yet: no backend is configured for Studio audio edits.'
      );
    }
    const accepted = await http.post<StudioActionJob>(`/songs/${songId}/studio/trim`, request);
    return waitForStudioAction<Song>(songId, accepted);
  },

  /** POST /songs/:id/studio/cut */
  async cut(songId: string, range: TrimRange, sourceVersionId?: string | null): Promise<Song> {
    const request = { songId, ...(sourceVersionId ? { sourceVersionId } : null), ...range } satisfies TrimRequest;
    if (!API_BASE_URL) {
      throw new UnsupportedCapabilityError(
        'cut',
        'Cutting audio is not available yet: no backend is configured for Studio audio edits.'
      );
    }
    const accepted = await http.post<StudioActionJob>(`/songs/${songId}/studio/cut`, request);
    return waitForStudioAction<Song>(songId, accepted);
  },

  /** POST /songs/:id/studio/split — one song becoming two new ones at a chosen point. */
  async split(songId: string, at: number, sourceVersionId?: string | null): Promise<SplitResult> {
    const request = { songId, ...(sourceVersionId ? { sourceVersionId } : null), at } satisfies SplitRequest;
    if (!API_BASE_URL) {
      throw new UnsupportedCapabilityError(
        'split',
        'Splitting audio is not available yet: no backend is configured for Studio audio edits.'
      );
    }
    const accepted = await http.post<StudioActionJob>(`/songs/${songId}/studio/split`, request);
    return waitForStudioAction<SplitResult>(songId, accepted);
  },

  /** POST /songs/:id/studio/crossfade — joins two songs' audio into a third, new song. */
  async crossfade(songId: string, secondSongId: string, seconds: number): Promise<Song> {
    const request = { songId, secondSongId, seconds } satisfies CrossfadeRequest;
    if (!API_BASE_URL) {
      throw new UnsupportedCapabilityError(
        'crossfade',
        'Crossfading audio is not available yet: no backend is configured for Studio audio edits.'
      );
    }
    const accepted = await http.post<StudioActionJob>(`/songs/${songId}/studio/crossfade`, request);
    return waitForStudioAction<Song>(songId, accepted);
  },

  /** POST /songs/:id/studio/insertSilence — splices a gap into a stored file at a chosen point. */
  async insertSilence(songId: string, at: number, duration: number, sourceVersionId?: string | null): Promise<Song> {
    const request = {
      songId,
      ...(sourceVersionId ? { sourceVersionId } : null),
      at,
      duration,
    } satisfies InsertSilenceRequest;
    if (!API_BASE_URL) {
      throw new UnsupportedCapabilityError(
        'insertSilence',
        'Inserting silence is not available yet: no backend is configured for Studio audio edits.'
      );
    }
    const accepted = await http.post<StudioActionJob>(`/songs/${songId}/studio/insertSilence`, request);
    return waitForStudioAction<Song>(songId, accepted);
  },

  /** POST /songs/:id/studio/fade — fades the stored file in at the start and out at the end. */
  async fade(songId: string, seconds: number, sourceVersionId?: string | null): Promise<Song> {
    const request = {
      songId,
      ...(sourceVersionId ? { sourceVersionId } : null),
      seconds,
    } satisfies FadeRequest;
    if (!API_BASE_URL) {
      throw new UnsupportedCapabilityError(
        'fade',
        'Fading audio is not available yet: no backend is configured for Studio audio edits.'
      );
    }
    const accepted = await http.post<StudioActionJob>(`/songs/${songId}/studio/fade`, request);
    return waitForStudioAction<Song>(songId, accepted);
  },

  /** POST /songs/:id/studio/normalize — normalizes a selected section to a target LUFS. */
  async normalize(
    songId: string,
    range: TrimRange,
    targetLufs: number,
    sourceVersionId?: string | null
  ): Promise<Song> {
    const request = {
      songId,
      ...(sourceVersionId ? { sourceVersionId } : null),
      ...range,
      targetLufs,
    } satisfies TrimRequest & { targetLufs: number };
    if (!API_BASE_URL) {
      throw new UnsupportedCapabilityError(
        'normalize',
        'Normalizing audio is not available yet: no backend is configured for Studio audio edits.'
      );
    }
    const accepted = await http.post<StudioActionJob>(`/songs/${songId}/studio/normalize`, request);
    return waitForStudioAction<Song>(songId, accepted);
  },

  /*
    THE REST OF THE SURFACE. Tempo, pitch and master are real now — local ffmpeg DSP behind
    `/songs/:id/studio/…`, the same shape as trim — so they `satisfies`-check their request
    and send it. The region/mix/export methods that remain below still take the arguments
    their panel validated, `satisfies`-check them and throw: those capabilities have no
    backend yet, and a caller cannot be written against them that appears to work.
  */

  /** POST /songs/:id/studio/tempo — time stretch to a new BPM, pitch preserved. */
  async setTempo(request: TempoRequest): Promise<Song> {
    void (request satisfies TempoRequest);
    if (!API_BASE_URL) {
      throw new UnsupportedCapabilityError(
        'tempo',
        'Changing tempo is not available yet: no backend is configured for Studio audio edits.'
      );
    }
    const accepted = await http.post<StudioActionJob>(`/songs/${request.songId}/studio/tempo`, request);
    return waitForStudioAction<Song>(request.songId, accepted);
  },

  /** POST /songs/:id/studio/pitch — shift by semitones, duration preserved. */
  async setPitch(request: PitchRequest): Promise<Song> {
    void (request satisfies PitchRequest);
    if (!API_BASE_URL) {
      throw new UnsupportedCapabilityError(
        'pitch',
        'Shifting pitch is not available yet: no backend is configured for Studio audio edits.'
      );
    }
    const accepted = await http.post<StudioActionJob>(`/songs/${request.songId}/studio/pitch`, request);
    return waitForStudioAction<Song>(request.songId, accepted);
  },

  /**
   * NOT IMPLEMENTED — POST /songs/:id/regions.
   *
   * One method for the remaining region operations rather than one near-identical throwing
   * function apiece: they differ only in the verb, they share a request shape, and a real
   * backend would almost certainly expose them as one endpoint discriminated by
   * `operation`. The capability key is taken from the operation so the error still
   * names the specific thing that is missing. `insertSilence` and `fade` used to be here
   * too; each now has its own real method above, the same way cut/split/crossfade did
   * before them.
   */
  async editRegion(request: RegionEditRequest): Promise<never> {
    void (request satisfies RegionEditRequest);
    const capability = AUDIO_EDIT_CAPABILITIES[request.operation];
    throw new UnsupportedCapabilityError(
      request.operation,
      `${capability.label} is not available yet: ${capability.missing} ` +
        'See src/api/audioEditApi.ts.'
    );
  },

  /** GET /songs/:id/studio/mix — analyzes the stored stems and returns their starting positions. */
  async getMixAnalysis(songId: string, sourceVersionId?: string | null): Promise<MixAnalysis> {
    if (!API_BASE_URL) {
      throw new UnsupportedCapabilityError(
        'mix',
        'Mix analysis is not available yet: no backend is configured for Studio audio edits.'
      );
    }
    const accepted = await http.get<StudioActionJob>(`/songs/${songId}/studio/mix`, {
      query: sourceVersionId ? { sourceVersionId } : undefined,
    });
    return waitForStudioAction<MixAnalysis>(songId, accepted);
  },

  /** POST /songs/:id/studio/mix. */
  async applyMix(request: MixRequest): Promise<Song> {
    void (request satisfies MixRequest);
    if (!API_BASE_URL) {
      throw new UnsupportedCapabilityError(
        'mix',
        'Mixing is not available yet: no backend is configured for Studio audio edits.'
      );
    }
    const accepted = await http.post<StudioActionJob>(`/songs/${request.songId}/studio/mix`, request);
    return waitForStudioAction<Song>(request.songId, accepted);
  },

  /** POST /songs/:id/studio/mix/preview — renders preview audio without saving a version. */
  async previewMix(request: MixRequest, signal?: AbortSignal): Promise<BlobResponse> {
    void (request satisfies MixRequest);
    if (!API_BASE_URL) {
      throw new UnsupportedCapabilityError(
        'mix',
        'Mix preview is not available yet: no backend is configured for Studio audio edits.'
      );
    }
    const accepted = await http.post<StudioActionJob>(
      `/songs/${request.songId}/studio/mix/preview`,
      request,
      signal ? { signal } : undefined
    );
    const finished = await waitForStudioJob(request.songId, accepted);
    if (!finished.resultAudioUrl) {
      throw new Error('Studio mix preview completed without audio.');
    }
    return http.getBlob(finished.resultAudioUrl.replace(/^\/api/, ''), signal ? { signal } : undefined);
  },

  /** GET /songs/:id/studio/master — measures the current source track for mastering. */
  async getMasterAnalysis(songId: string, sourceVersionId?: string | null): Promise<MasterAnalysis> {
    if (!API_BASE_URL) {
      throw new UnsupportedCapabilityError(
        'master',
        'Mastering analysis is not available yet: no backend is configured for Studio audio edits.'
      );
    }
    const accepted = await http.get<StudioActionJob>(`/songs/${songId}/studio/master`, {
      query: sourceVersionId ? { sourceVersionId } : undefined,
    });
    return waitForStudioAction<MasterAnalysis>(songId, accepted);
  },

  /** POST /songs/:id/studio/master. */
  async master(request: MasterRequest): Promise<Song> {
    void (request satisfies MasterRequest);
    if (!API_BASE_URL) {
      throw new UnsupportedCapabilityError(
        'master',
        'Mastering is not available yet: no backend is configured for Studio audio edits.'
      );
    }
    const accepted = await http.post<StudioActionJob>(`/songs/${request.songId}/studio/master`, request);
    return waitForStudioAction<Song>(request.songId, accepted);
  },

  /**
   * Returns the real download URL for a stored export or stem asset.
   *
   * With no backend configured this still throws `UnsupportedCapabilityError`, which keeps
   * callers on their honest placeholder path. With a backend configured it assembles the
   * authenticated media route the browser should save from — individual stems stay on their
   * WAV route and the all-stems request points at `/stems.zip`.
   */
  async listAvailableStems(songId: string, sourceVersionId?: string | null): Promise<StoredStemType[]> {
    return (await this.getStemAvailability(songId, sourceVersionId)).stemTypes;
  },

  async getStemAvailability(songId: string, sourceVersionId?: string | null): Promise<StemAvailabilityResult> {
    if (!API_BASE_URL) {
      throw new UnsupportedCapabilityError(
        'separateStems',
        'Stem downloads are not available yet: no backend is configured for Studio stems.'
      );
    }
    return http.get<StemAvailabilityResult>(`/songs/${songId}/studio/stems`, {
      query: sourceVersionId ? { sourceVersionId } : undefined,
    });
  },

  async exportAudio(request: ExportAudioRequest): Promise<ExportAudioResult> {
    void (request satisfies ExportAudioRequest);
    if (!API_BASE_URL) {
      throw new UnsupportedCapabilityError(
        'exportAudio',
        'Exporting audio is not available yet: no backend is configured for audio downloads.'
      );
    }

    const stemQuery = request.stemJobId
      ? `?stemJobId=${encodeURIComponent(request.stemJobId)}`
      : '';
    const path =
      request.content === 'stems'
        ? request.stemName
          ? `/media/audio/${request.songId}/stems/${request.stemName}${stemQuery}`
          : `/media/audio/${request.songId}/stems.zip${stemQuery}`
        : `/media/audio/${request.songId}/export?format=${request.format}`;

    return {
      url: `${API_BASE_URL}${path}`,
      filename:
        request.content === 'stems'
          ? request.stemName
            ? `${request.stemName}.wav`
            : 'stems.zip'
          : `${request.songId}.${request.format}`,
      format: request.format,
    };
  },
};
